import { Formik, FormikActions, FormikErrors, FormikProps } from 'formik';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { RenderChallenge } from '../../../utils/challengerMain';
import { COLORS, FONT_FAMILY, MQS } from '../constants/styles';
import Button from './Button';

export interface IFormProps<T> {
    id?: string;
    buttonTextLabel: string;
    renderExtraButtons?: (values: T) => ReactNode;
    renderFormFields?: (values: T) => ReactNode;
    renderExtraFields?: (values: T) => ReactNode;
    renderBeforeButton?: (values: T) => ReactNode;
    value: T;
    handleSubmit: (values: T, actions: FormikActions<T>) => any;
    loading: boolean;
    saveButtonProps?: any;
    showInvalidFieldsNotification?: boolean;
    showWarning?: (message: ReactNode) => any;
    isTargetFieldChanged?: boolean;
    qaId?: string;
    challengerPid?: string;
    challengerReadyCallback?: (ready: boolean) => void;
    onSubmitAttempt?: () => void;
    onSubmitAttemptWithErrors?: (values: T, errors: FormikErrors<T>) => void;
}

interface IFormState<T> {
    isSubmitting: boolean;
    isChallengeLoaded: boolean;
}

const FormWrapper = styled.div`
    margin-top: 16px;
    position: relative;

    & > div:last-child {
        margin-top: 24px;
    }
`;

export const ButtonsWrapper = styled.div`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-top: 35px;
    width: 100%;

    @media (max-width: ${MQS.mq2Min}) {
        & > button {
            margin: 10px 0;
            font-size: 0.875rem;
            width: 100%;
        }
    }
`;

const ChallengerWrapper = styled.div`
    input.c-input {
        background-color: transparent;
        border: none;
        border-bottom: 1px solid;
        border-bottom-color: ${COLORS.grey3};
        border-radius: 0;
        color: ${COLORS.dark};
        display: block;
        ${FONT_FAMILY.multipleRegularCss};
        font-weight: 300;
        padding: 1px 1px 5px 1px;
        width: 100%;

        :placeholder-shown {
            padding-left: 0px;

            @media (min-width: ${MQS.mq1Min}) {
                font-size: 0.875rem;
            }

            @media (min-width: ${MQS.mq2Max}) {
                font-size: 1rem;
            }
        }

        @media (max-width: ${MQS.mq2Min}) {
            padding-left: 0;
        }

        :focus {
            outline: none;
            border-bottom: 2px solid ${COLORS.rakutenRed};
        }

        [disabled] {
            opacity: 1; // required for iOS safari;
            -webkit-text-fill-color: ${COLORS.grey3};
        }
    }
`;

class Form<T> extends React.Component<IFormProps<T>, IFormState<T>> {
    constructor(props: IFormProps<T>) {
        super(props);

        const { loading, challengerPid } = this.props;

        this.state = {
            isChallengeLoaded: !challengerPid,
            isSubmitting: loading,
        };

        this.renderForm = this.renderForm.bind(this);
        this.onChallengeReady = this.onChallengeReady.bind(this);
    }

    public componentDidUpdate(prevProps: Readonly<IFormProps<T>>): void {
        const { loading } = this.props;
        if (loading !== prevProps.loading) {
            this.setState({ ...this.state, isSubmitting: loading });
        }
    }

    private onChallengeReady = (ready: boolean) => {
        if (this.props.challengerReadyCallback) {
            this.props.challengerReadyCallback(ready);
        }
        this.setState({ ...this.state, isChallengeLoaded: ready });
    };

    public render() {
        const { value, handleSubmit } = this.props;
        return (
            <Formik
                enableReinitialize={true}
                initialValues={value}
                onSubmit={handleSubmit}
                render={this.renderForm}
            />
        );
    }

    private renderChallenge(challengerPid: string) {
        return (
            <ChallengerWrapper>
                <RenderChallenge
                    cpid={challengerPid}
                    isReadyCallback={this.onChallengeReady}
                    refreshOnDerender={true}
                />
            </ChallengerWrapper>
        );
    }

    private renderForm({
        values,
        submitForm,
        setTouched,
        validateForm,
    }: FormikProps<T>): ReactNode {
        const {
            id,
            value,
            buttonTextLabel,
            renderFormFields,
            renderExtraButtons,
            renderExtraFields,
            renderBeforeButton,
            saveButtonProps,
            showInvalidFieldsNotification,
            showWarning,
            isTargetFieldChanged = true,
            qaId,
            challengerPid,
            children,
        } = this.props;
        const { isSubmitting, isChallengeLoaded } = this.state;
        const isDisabled = isSubmitting || !isChallengeLoaded || !isTargetFieldChanged;

        const submit = async () => {
            if (isSubmitting) {
                return;
            }

            const errors: FormikErrors<T> = await validateForm();

            if (!Object.keys(errors).length) {
                this.setState({ ...this.state, isSubmitting: true });
                submitForm();
            } else {
                if (this.props.onSubmitAttemptWithErrors) {
                    this.props.onSubmitAttemptWithErrors(values, errors);
                } else if (this.props.onSubmitAttempt) {
                    this.props.onSubmitAttempt();
                }

                const touched: any = {};
                Object.keys(value).forEach((key) => (touched[key] = true));
                setTouched(touched);
                if (showInvalidFieldsNotification) {
                    showWarning &&
                        showWarning(TranslationHelpers.getFormattedMessage('error.unknown'));
                }
            }
        };

        return (
            <FormWrapper id={id} data-qa-id={`${qaId}-form`}>
                {children}
                {renderFormFields && renderFormFields(values)}
                {challengerPid && this.renderChallenge(challengerPid)}
                {renderBeforeButton && renderBeforeButton(values)}
                <ButtonsWrapper>
                    {renderExtraButtons && renderExtraButtons(values)}
                    <Button
                        text={TranslationHelpers.getMessageDescriptor(buttonTextLabel)}
                        primary={true}
                        type="submit"
                        id="submit"
                        loading={isSubmitting}
                        disabled={isDisabled}
                        success={false}
                        icon={null}
                        onClick={submit}
                        {...saveButtonProps}
                    />
                </ButtonsWrapper>
                {renderExtraFields && renderExtraFields(values)}
            </FormWrapper>
        );
    }
}

export default Form;
